import { createColumnHelper } from '@tanstack/react-table'
import { UserGroupPolicy } from '../../types/GroupTypes'
import { Tooltip } from '@radix-ui/themes'
import {
  IconAlertTriangle,
  IconBrandAndroid,
  IconBrandApple,
  IconFaceId,
  IconLineScan,
  IconLockAccess,
  IconMapPin,
  IconMessageCancel,
  IconShieldDown,
} from '@tabler/icons-react'
import { CellContext } from '@tanstack/react-table'

export const managePolicyColumns = (
  checkedItems: Record<number, boolean>,
  setCheckedItems: (checkedItems: Record<number, boolean>) => void,
  handleCheckboxChange: (id: number) => void
) => {
  const columnHelper = createColumnHelper<UserGroupPolicy>()

  const columnHelpers = [
    {
      id: 'selection',
      cell: (row: CellContext<UserGroupPolicy, string>) => (
        <input
          key={row.row.original.id}
          id={'assigned_policy_' + row.row.original.id}
          type="checkbox"
          className={`h-8 w-8 text-blue-600 rounded focus:ring-mainPurple checked:bg-mainPurple dark:bg-transparent dark:checked:bg-mainPurple scale-50 cursor-pointer border-gray-800 dark:border-gray-200`}
          checked={checkedItems[row.row.original.id]}
          onChange={() => {
            setCheckedItems({
              ...checkedItems,
              [row.row.original.id]: !checkedItems[row.row.original.id],
            })
            handleCheckboxChange(row.row.original.id)
          }}
        />
      ),
    },
    columnHelper.accessor('name', {
      header: 'Policy Name',
    }),
    columnHelper.accessor('state', {
      cell: (row) => renderActive(row.row.original.state),
      header: 'State',
    }),
    columnHelper.accessor('actions', {
      header: 'Actions',
    }),
    columnHelper.accessor('alertSeverity', {
      header: 'Rules',
      cell: (row: { row: { original: UserGroupPolicy } }) => buildRuleIcons(row.row.original),
    }),
  ]

  const buildRuleIcons = (userGroupPolicy: UserGroupPolicy) => {
    const isDeviceIntegrityEnabled =
      userGroupPolicy.deviceIntegrityPeriod && userGroupPolicy.deviceIntegrityPeriod !== 'NEVER'
    const isScreenLockEnabled = userGroupPolicy.screenLockEnabled
    const isSmishingEnabled = userGroupPolicy.smishingEnabled
    const isBiometricsEnabled = userGroupPolicy.biometricsEnabled
    const isAlertSeverityEnabled = userGroupPolicy.alertSeverity !== 'NONE'
    const isTravelEnabled = userGroupPolicy.travelEnabled

    const isAndroidVersionEnabled = userGroupPolicy.androidMinimumVersion
    const isAndroidSecurityPatchEnabled = userGroupPolicy.androidLastPatchVersionGracePeriod

    const isIOSNMinusEnabled = userGroupPolicy.iosMinimumVersion || userGroupPolicy.iosVersionOffset

    return (
      <div className="flex gap-2">
        <Tooltip content="Android Outdated OS">
          <IconBrandAndroid key="outdatedOS" className={isAndroidVersionEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Android Security Patch">
          <IconShieldDown
            key="outdatedOS"
            className={isAndroidSecurityPatchEnabled ? 'text-mainText' : 'text-neutral-8'}
          />
        </Tooltip>

        <Tooltip content="iOS Outdated OS">
          <IconBrandApple key="outdatedOS" className={isIOSNMinusEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Biometrics">
          <IconFaceId key="biometrics" className={isBiometricsEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Smishing">
          <IconMessageCancel key="smishing" className={isSmishingEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Screen Lock">
          <IconLockAccess key="screenLock" className={isScreenLockEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>

        <Tooltip content="Device Check In">
          <IconLineScan
            key="deviceIntegrity"
            className={isDeviceIntegrityEnabled ? 'text-mainText' : 'text-neutral-8'}
          />
        </Tooltip>

        <Tooltip content="Alert Severity">
          <IconAlertTriangle
            key="alertSeverity"
            className={isAlertSeverityEnabled ? 'text-mainText' : 'text-neutral-8'}
          />
        </Tooltip>

        <Tooltip content="Sensitive Travel">
          <IconMapPin key="travel" className={isTravelEnabled ? 'text-mainText' : 'text-neutral-8'} />
        </Tooltip>
      </div>
    )
  }

  const renderActive = (active: boolean) => {
    return (
      <div className="h-4 py-[3px] inline-flex justify-start items-center gap-2">
        {active ? (
          <div className="w-2 h-2 bg-successGreen rounded-full"></div>
        ) : (
          <div className="w-2 h-2 bg-[#737373] rounded-full"></div>
        )}
        <div className="justify-start text-text-Neutral-Secondary text-sm">{active ? 'Active' : 'Inactive'}</div>
      </div>
    )
  }

  return columnHelpers
}
